import React, { useMemo, useCallback, useEffect } from "react";
import ImageComponent from "@/components/ui/image";
import { useUser } from "@/contexts/user-context";
import { useRouter } from "next/navigation";
import { useLockBodyScroll } from "@/utils/hooks/useLockBodyScroll";
import { Button } from "@/components/ui/button";
import { X, Plus, Trash2, AlertTriangle, Truck } from "lucide-react";
import { sendGAEvent } from "@next/third-parties/google";
import { MINIMUM_ORDER_VALUE } from "@/constants/cart";
import CartEmptyPlaceholder from "@/components/checkout/CartemptyPlaceholder";
import { useCheckout } from "@/contexts/checkout";
import Link from "next/link";
import CustomisationOptions from "./CustomisationOptions";
import { motion, AnimatePresence } from "framer-motion";
interface CartItem {
  product_id: number;
  name: string;
  vendor_id: number;
  vendor_name: string;
  prod_img: string | null;
  count: number;
  final_price_calculated: string;
  isEggless: boolean;
  customizations: any[];
  note?: string;
  slug?: string;
  selectedVariant: any;
  selectedFlavor: any;
  uniqueId: string;
  vendor_details: {
    slug: string;
  };
}
const defaultCartItems: CartItem[] = [];
const CartDrawer: React.FC = () => {
  const {
    isAuthenticated
  } = useUser();
  const {
    lowCartValueFee,
    clearCart,
    bulkRemoveProducts,
    updateProductQuantity,
    products: cartItems = defaultCartItems,
    isCartOpen: isOpen,
    setIsCartOpen,
    vendorDetails
  } = useCheckout();
  const router = useRouter();
  const onClose = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Get vendor name from first item
  const vendorName = cartItems.length > 0 && cartItems[0] ? cartItems[0].vendor_name : "";
  const handleAddItems = () => {
    if (cartItems.length > 0 && cartItems[0]) {
      router.push(`/${cartItems[0].vendor_details?.slug}/${cartItems[0].vendor_id}`);
      onClose();
    }
  };
  const handleClearCart = async () => {
    try {
      await clearCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
      // You might want to show a toast notification here
    }
  };
  const handleProductClick = (e: React.MouseEvent, item: CartItem) => {
    e.preventDefault();
    router.push(`/${item.vendor_details?.slug}/product/${item.slug}`);
    onClose();
  };
  const handleQuanityIncrement = (item_0: CartItem) => {
    updateProductQuantity({
      product: item_0,
      quantity: 1
    });
  };
  const handleQuanityDecrement = (item_1: CartItem) => {
    updateProductQuantity({
      product: item_1,
      quantity: 1,
      action: "remove"
    });
  };
  const getItemPrice = (item_2: CartItem) => {
    const price = parseFloat(item_2.final_price_calculated || "0");
    return price;
  };
  const totalProductPrice = useMemo(() => {
    return cartItems.reduce((total, item_3) => {
      return total + parseFloat(item_3.final_price_calculated || "0");
    }, 0);
  }, [cartItems]);

  // Determine if low cart value warning should be shown
  const shouldShowLowCartWarning = useMemo(() => {
    if (!isAuthenticated) {
      // For logged-out users, use the existing logic with MINIMUM_ORDER_VALUE
      return totalProductPrice > 0 && totalProductPrice < MINIMUM_ORDER_VALUE;
    } else {
      // For logged-in users, only show warning if low_cart_value_fee has a value
      return totalProductPrice > 0 && lowCartValueFee > 0;
    }
  }, [isAuthenticated, totalProductPrice, lowCartValueFee]);

  // Calculate the amount needed to reach minimum order value
  const amountNeeded = useMemo(() => {
    if (!isAuthenticated) {
      return Math.max(0, MINIMUM_ORDER_VALUE - totalProductPrice);
    } else {
      // For logged-in users, we don't show the amount needed since we only show the warning
      // when low_cart_value_fee has a value (indicating the fee will be applied)
      return 0;
    }
  }, [isAuthenticated, totalProductPrice]);

  // Lock background scroll when cart is open
  useLockBodyScroll(isOpen);

  // GA Analytics: view_cart when drawer opens
  useEffect(() => {
    if (isOpen) {
      sendGAEvent("event", "view_cart", {
        currency: "INR",
        value: totalProductPrice,
        items: cartItems.map(item_4 => ({
          item_id: String(item_4.product_id),
          item_name: item_4.name,
          item_brand: item_4.vendor_name,
          price: parseFloat(item_4.final_price_calculated) / item_4.count,
          quantity: item_4.count
        }))
      });
    }
  }, [isOpen]);
  // useEffect(() => {
  //   if (isOpen) {
  //     // Add global function for edit note
  //     (window as any).editNote = (uniqueId: string) => {
  //       const item = cartItems.find(
  //         (cartItem) => getCartItemUniqueId(cartItem) === uniqueId,
  //       );
  //       if (item) {
  //         handleEditNote(item);
  //       }

  //       // Use setTimeout to ensure DOM elements are rendered
  //       const timeoutId = setTimeout(() => {
  //         tippy("[data-tippy-content]", {
  //           theme: "dark",
  //           placement: "top",
  //           arrow: true,
  //           animation: "fade",
  //           duration: [200, 200],
  //           allowHTML: true,
  //           interactive: true,
  //           interactiveBorder: 10,
  //           trigger: "mouseenter focus",
  //           hideOnClick: false,
  //         });
  //       }, 100);

  //       return () => {
  //         clearTimeout(timeoutId);
  //         const tooltips = tippy("[data-tippy-content]");
  //         tooltips.forEach((tooltip) => tooltip.destroy());
  //         delete (window as any).editNote;
  //       };
  //     };
  //     return undefined;
  //   }
  // }, [isOpen, cartItems, handleEditNote]);

  const hasFreeShippingThreshold = !!vendorDetails?.free_shipping_threshold && Number(vendorDetails.free_shipping_threshold) > 0;
  return <AnimatePresence>
      {isOpen && <motion.div key="cart-backdrop" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: "easeOut"
    }} className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[500] touch-none" onClick={onClose} />}

      {isOpen && <motion.div key="cart-drawer" initial={{
      x: "100%"
    }} animate={{
      x: 0
    }} exit={{
      x: "100%"
    }} transition={{
      type: "spring",
      damping: 25,
      stiffness: 180,
      mass: 0.8
    }} className="fixed top-0 right-0 h-full w-full md:w-[400px] bg-white shadow-2xl z-[501] overflow-hidden overscroll-contain will-change-transform">
          {cartItems.length > 0 ? <div className="flex flex-col h-full">
              {/* ... existing items list ... */}
              <div className="flex items-center justify-between p-4 flex-shrink-0">
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1" aria-label="Close cart">
                  <X className="w-6 h-6" />
                </button>

                {cartItems.length > 0 && <button onClick={handleClearCart} className="text-red-600 hover:text-red-700 font-medium text-sm transition-colors">
                    Clear Cart
                  </button>}
              </div>

              {/* Header */}
              <div className="px-4 py-3 flex-shrink-0">
                <h2 className="text-xl font-light font-serif text-gray-900">
                  {vendorName || "Your Cart"}
                </h2>
              </div>

              {/* Add Items Button in Capsule */}
              {cartItems.length > 0 && <div className="px-4 py-3 flex-shrink-0">
                  <Button onClick={handleAddItems} className="w-full flex items-center justify-center space-x-2 text-[#037a6a] font-semibold py-3 px-4 rounded-full transition-all duration-200 border-2 border-[#037a6a] hover:bg-[#037a6a]/5">
                    <Plus className="w-5 h-5" />
                    <span>ADD ITEMS</span>
                  </Button>
                </div>}

              {/* Cart Items List - Scrollable Area */}
              <div className="flex-1 overflow-hidden relative">
                <motion.div initial="hidden" animate="show" variants={{
            hidden: {
              opacity: 0
            },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.05
              }
            }
          }} className="h-full overflow-y-auto p-4 space-y-4 pb-16 scrollbar-thin scrollbar-thumb-gray-200">
                  {cartItems.toSorted((a, b) => a.uniqueId.localeCompare(b.uniqueId)).map(item_5 => <motion.div key={item_5.uniqueId} variants={{
              hidden: {
                opacity: 0,
                y: 20
              },
              show: {
                opacity: 1,
                y: 0
              }
            }} className="flex items-start space-x-4 border-b border-gray-100 pb-4 last:border-b-0">
                        <Link className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-gray-100 cursor-pointer hover:opacity-90 transition-opacity" href={`/${item_5.vendor_details?.slug}/product/${item_5.slug}`} onClick={e_0 => handleProductClick(e_0, item_5)}>
                          {item_5.prod_img ? <ImageComponent src={item_5.prod_img} alt={item_5.name} width={80} height={80} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-400 text-xs font-medium">
                              No Image
                            </div>}
                        </Link>
                        <div className="flex flex-col justify-between flex-1 min-h-[80px]">
                          <div className="flex items-start justify-between gap-2">
                            <Link className="text-sm md:text-base font-normal text-gray-900 cursor-pointer hover:text-[#037a6a] transition-colors line-clamp-2" href={`/${item_5.vendor_details?.slug}/product/${item_5.slug}`} onClick={e_1 => handleProductClick(e_1, item_5)}>
                              {item_5.name}
                            </Link>
                            <Button onClick={() => bulkRemoveProducts({
                    productsToBeRemoved: [item_5]
                  })} variant="ghost" size="sm" className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 h-auto rounded-full transition-colors" aria-label="Remove item">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="text-[10px] text-gray-500 mt-1">
                            <CustomisationOptions item={item_5 as any} />
                          </div>

                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center bg-gray-50 rounded-full px-2 py-1 border border-gray-100">
                              <button onClick={() => handleQuanityDecrement(item_5)} className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors text-lg">
                                -
                              </button>
                              <span className="text-sm font-bold text-gray-900 min-w-[30px] text-center">
                                {item_5.count}
                              </span>
                              <button onClick={() => handleQuanityIncrement(item_5)} className="w-6 h-6 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors text-lg">
                                +
                              </button>
                            </div>
                            <p className="text-base font-bold text-gray-900">
                              ₹{getItemPrice(item_5).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </motion.div>)}

                  {/* Free Delivery Progress Banner */}
                  {hasFreeShippingThreshold && totalProductPrice < Number(vendorDetails!.free_shipping_threshold) && <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="bg-emerald-50/50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-4 mt-2">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Truck className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div className="text-sm text-gray-700">
                          Add items worth{" "}
                          <span className="text-emerald-700 font-bold text-lg">
                            ₹{(Number(vendorDetails.free_shipping_threshold) - totalProductPrice).toFixed(0)}
                          </span>{" "}
                          to get free delivery
                        </div>
                      </motion.div>}
                </motion.div>

                {/* Fade Effect Overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>

              {/* Checkout Section - Sticky Footer */}
              <div className="p-6 border-t border-gray-100 bg-white flex-shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                    Subtotal
                  </span>
                  <div className="text-right">
                    <span className="text-xl font-black text-gray-900">
                      ₹{totalProductPrice.toLocaleString()}
                    </span>
                    <p className="text-[10px] text-gray-400 font-medium">Plus taxes & charges</p>
                  </div>
                </div>

                {/* Low Cart Warning */}
                {shouldShowLowCartWarning && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: "auto",
            opacity: 1
          }} className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-amber-800 font-bold">Low Cart Value</p>
                      <p className="text-[10px] text-amber-700 mt-0.5">
                        {!isAuthenticated ? `Add ₹${amountNeeded.toFixed(2)} more to reach minimum order value of ₹${MINIMUM_ORDER_VALUE}` : `A low cart value fee of ₹${Number(lowCartValueFee || 0).toFixed(2)} will be applied.`}
                      </p>
                    </div>
                  </motion.div>}
                
                <Button onClick={() => {
            onClose();
            router.push("/checkout");
          }} disabled={cartItems.length === 0} className={`w-full py-6 font-bold !font-serif rounded-2xl transition-all shadow-lg text-lg tracking-widest ${cartItems.length === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-[#037a6a] text-white hover:bg-[#026b5c] hover:shadow-xl active:scale-[0.98]"}`}>
                  CHECKOUT
                </Button>
              </div>
            </div> : <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="h-full relative">
              <CartEmptyPlaceholder onClose={onClose} />
              <button onClick={onClose} className="absolute top-6 left-6 text-gray-400 hover:text-gray-900 transition-colors p-2 rounded-full hover:bg-gray-100" aria-label="Close cart">
                <X className="w-6 h-6" />
              </button>
            </motion.div>}
        </motion.div>}
    </AnimatePresence>;
};
export default CartDrawer;